import React, { useState } from 'react';
import { ethers } from 'ethers';
import './App.css';

const BioFlywheelScorer = () => {
  const [tokenAddress, setTokenAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [scores, setScores] = useState(null);
  const [error, setError] = useState('');

  const CONTRACT_ADDRESS = '0x0fB8fF59a808fAdA63826AA826dEf78133697c0D';
  const RPC_URL = 'https://sepolia.base.org';
  
  const CONTRACT_ABI = [
    "function getTokenHealth(address token) view returns (uint8 metabolicStability, uint8 immuneSurveillance, uint8 angiogenesisHealth, uint8 metastaticPotential, uint8 evolutionaryFitness, uint8 finalScore, uint32 timestamp, uint8 scoreVersion, bool isInitialized)",
    "function getHealthCategory(address token) view returns (string)"
  ];

  // Example tokens on Base Sepolia
  const exampleTokens = [
    {
      name: 'USDC',
      address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      emoji: '💵',
      description: 'Stablecoin - Expected high metabolic stability'
    }
  ];

  const getHealthColor = (score) => {
    if (score >= 80) return { bg: '#10b981', light: '#d1fae5' };
    if (score >= 60) return { bg: '#3b82f6', light: '#dbeafe' };
    if (score >= 40) return { bg: '#f59e0b', light: '#fef3c7' };
    return { bg: '#ef4444', light: '#fee2e2' };
  };

  const getCategoryStyle = (category) => {
    const styles = {
      'Resilient': { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', shadow: '0 4px 20px rgba(16, 185, 129, 0.4)' },
      'Stable': { bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', shadow: '0 4px 20px rgba(59, 130, 246, 0.4)' },
      'Volatile': { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', shadow: '0 4px 20px rgba(245, 158, 11, 0.4)' },
      'Fragile': { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', shadow: '0 4px 20px rgba(239, 68, 68, 0.4)' }
    };
    return styles[category] || styles['Stable'];
  };

 const fetchScores = async (address = tokenAddress) => {
    const addressToUse = address || tokenAddress;
    
    if (!addressToUse || !ethers.isAddress(addressToUse)) {
      setError('Please enter a valid token address');
      return;
    }

    setLoading(true);
    setError('');
    setScores(null);

    try {
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);
      
      // Try to get health data
      let health;
      try {
        health = await contract.getTokenHealth(addressToUse);
      } catch (contractError) {
        // If contract call fails, show the "not scored yet" message
        setError('⚠️ This token hasn\'t been scored yet. Currently only pre-scored demo tokens are available. Try the USDC button above. Mainnet launch will enable on-demand scoring for any token on Base.');
        setLoading(false);
        return;
      }
      
      // Check if token has been initialized
      if (!health.isInitialized) {
        setError('⚠️ This token hasn\'t been scored yet. Currently only pre-scored demo tokens are available. Try the USDC button above. Mainnet launch will enable on-demand scoring for any token on Base.');
        setLoading(false);
        return;
      }

      const category = await contract.getHealthCategory(addressToUse);

      setScores({
        metabolic: Number(health.metabolicStability),
        immune: Number(health.immuneSurveillance),
        angiogenesis: Number(health.angiogenesisHealth),
        metastatic: Number(health.metastaticPotential),
        evolutionary: Number(health.evolutionaryFitness),
        final: Number(health.finalScore),
        category: category,
        timestamp: new Date(Number(health.timestamp) * 1000),
        tokenAddress: addressToUse
      });
    } catch (err) {
      // Generic error fallback
      setError('⚠️ Unable to connect to the blockchain. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };
  const loadExampleToken = (address) => {
    setTokenAddress(address);
    fetchScores(address);
  };

  const MetricIcon = ({ type }) => {
    const icons = {
      metabolic: '⚡',
      immune: '🛡️',
      angiogenesis: '💉',
      metastatic: '🔬',
      evolutionary: '🧬'
    };
    return <span className="metric-icon">{icons[type]}</span>;
  };

  const ScoreBar = ({ label, score, description, weight, inverted = false, type }) => {
    const displayScore = inverted ? 100 - score : score;
    const colors = getHealthColor(displayScore);
    
    return (
      <div className="score-card">
        <div className="score-header">
          <div className="score-title">
            <MetricIcon type={type} />
            <span className="score-label">{label}</span>
            <span className="score-weight">{weight}%</span>
          </div>
          <div className="score-value" style={{ color: colors.bg }}>
            {displayScore}
          </div>
        </div>
        <div className="progress-container">
          <div 
            className="progress-bar"
            style={{ 
              width: `${displayScore}%`,
              background: `linear-gradient(90deg, ${colors.bg} 0%, ${colors.bg}dd 100%)`,
            }}
          >
            <div className="progress-shine"></div>
          </div>
        </div>
        <p className="score-description">{description}</p>
      </div>
    );
  };

  return (
    <div className="app-container">
      <div className="content-wrapper">
        
        {/* Testnet Banner */}
        <div className="testnet-banner">
          🧪 <strong>Demo Mode:</strong> Running on Base Sepolia Testnet
        </div>

        {/* Coming Soon Banner */}
        <div className="coming-soon-banner">
          <div className="coming-soon-content">
            <span className="coming-soon-icon">🚀</span>
            <div>
              <strong>Mainnet Launch Coming Soon!</strong>
              <p>Analyze any token on Base mainnet with on-demand scoring.</p>
            </div>
          </div>
        </div>

        {/* Header */}
        <header className="header">
          <div className="dna-helix">🧬</div>
          <h1 className="title">
            BioFlywheel Token Scorer
          </h1>
          <p className="subtitle">
            Cancer biology-inspired token health assessment using metabolic resilience,
            immune surveillance, and evolutionary fitness principles
          </p>
          <a 
            href="https://equationsinkala.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="learn-link"
          >
            Learn the Methodology →
          </a>
        </header>

        {/* Example Tokens */}
       
        {/* Example Tokens */}
        <div className="example-tokens-card">
          <h3 className="example-title">📊 Try a Demo Token:</h3>
          <div className="example-buttons">
            {exampleTokens.map((token) => (
              <button
                key={token.address}
                onClick={() => loadExampleToken(token.address)}
                className="example-button"
                disabled={loading}
              >
                <span className="example-emoji">{token.emoji}</span>
                <div className="example-text">
                  <span className="example-name">{token.name}</span>
                  <span className="example-desc">{token.description}</span>
                </div>
              </button>
            ))}
          </div>
          <p className="example-hint">
            💡 <strong>Note:</strong> This demo uses USDC (a stablecoin) which naturally scores high on metabolic stability. 
            Mainnet launch will include volatile assets, DeFi tokens, meme coins, and blue chips for comprehensive analysis.
          </p>
        </div>

        {/* Input Section */}
        <div className="input-card">
          <label className="input-label">
            Or Enter Any Token Address (Base Sepolia)
          </label>
          <div className="input-group">
            <input
              type="text"
              placeholder="0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
              className="token-input"
              onKeyPress={(e) => e.key === 'Enter' && fetchScores()}
            />
            <button
              onClick={() => fetchScores()}
              disabled={loading}
              className="analyze-button"
            >
              {loading ? (
                <><span className="spinner"></span> Analyzing...</>
              ) : (
                <>🔍 Analyze</>
              )}
            </button>
          </div>
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}
        </div>

        {/* Results Section */}
        {scores && (
          <div className="results-container">
            
            {/* Overall Score Card */}
            <div className="overall-score-card">
              <div 
                className="score-badge"
                style={{
                  background: getCategoryStyle(scores.category).bg,
                  boxShadow: getCategoryStyle(scores.category).shadow
                }}
              >
                <div className="final-score">{scores.final}</div>
                <div className="score-denominator">/100</div>
              </div>
              <div className="category-badge">
                {scores.category}
              </div>
              <div className="timestamp">
                Last Updated: {scores.timestamp.toLocaleDateString()} at {scores.timestamp.toLocaleTimeString()}
              </div>
              <div className="token-info">
                Token: <code>{scores.tokenAddress.slice(0, 6)}...{scores.tokenAddress.slice(-4)}</code>
              </div>
            </div>

            {/* Component Scores */}
            <div className="components-section">
              <h2 className="section-title">
                <span className="title-icon">📊</span>
                BioFlywheel Components
              </h2>
              
              <div className="scores-grid">
                <ScoreBar
                  type="metabolic"
                  label="Metabolic Stability"
                  score={scores.metabolic}
                  weight={30}
                  description="Energy efficiency under stress. Stable tokens maintain consistent price/volume ratios without panic-burning resources."
                />

                <ScoreBar
                  type="immune"
                  label="Immune Surveillance"
                  score={scores.immune}
                  weight={25}
                  description="Manipulation resistance through distributed liquidity. Healthy tokens resist single-wallet control."
                />

                <ScoreBar
                  type="angiogenesis"
                  label="Angiogenesis Health"
                  score={scores.angiogenesis}
                  weight={20}
                  description="Sustainable capital flow. Consistent volume indicates healthy ecosystem development."
                />

                <ScoreBar
                  type="metastatic"
                  label="Metastatic Risk"
                  score={scores.metastatic}
                  weight={15}
                  inverted={true}
                  description="Supply centralization risk. Lower scores indicate less concentrated token supplies."
                />

                <ScoreBar
                  type="evolutionary"
                  label="Evolutionary Fitness"
                  score={scores.evolutionary}
                  weight={10}
                  description="Survival evidence. Tokens that persist through market cycles demonstrate proven resilience."
                />
              </div>
            </div>

            {/* Methodology Footer */}
            <div className="methodology-card">
              <h3 className="methodology-title">
                💡 About the BioFlywheel Methodology
              </h3>
              <p className="methodology-text">
                This scoring system applies cancer biology principles to token analysis. 
                Rather than predicting price, it assesses <strong>structural resilience</strong>—the 
                same metabolic efficiency, immune surveillance, and survival mechanisms that 
                determine which organisms persist through hostile environments.
              </p>
              <a 
                href="https://equationsinkala.com" 
                className="methodology-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn Systems Thinking in Markets →
              </a>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="footer">
          <p>Educational demo on Base Sepolia testnet. Not financial advice.</p>
          <p className="contract-address">
            Contract: <code>{CONTRACT_ADDRESS}</code>
          </p>
          <p className="creator">
            Created by <a href="https://equationsinkala.com" target="_blank" rel="noopener noreferrer">
              Equations in Kala
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default BioFlywheelScorer;
